import { Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";

import { cn } from "~/lib/cn";
import {
  QuickMatchContext,
  type QuickMatchState,
} from "~/lib/discovery/quick-match-context";
import { createNoIndexMeta } from "~/lib/shared/meta";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/_public/discovery/match")({
  head: () =>
    createNoIndexMeta(
      "Quick Coach Match — Dream Coach Match",
      "Answer 2 quick questions to find your ideal coach.",
    ),
  component: QuickMatchLayout,
});

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

function QuickMatchLayout() {
  const location = useLocation();
  const [state, setState] = useState<QuickMatchState>({});

  const update = useCallback(
    (patch: QuickMatchState) => setState((prev) => ({ ...prev, ...patch })),
    [],
  );

  const contextValue = useMemo(() => ({ state, update }), [state, update]);

  const isContextStep = location.pathname.includes("/match/context");
  const currentStep = isContextStep ? 1 : 0;

  return (
    <QuickMatchContext.Provider value={contextValue}>
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Minimal 2-step progress */}
        <div className="flex items-center gap-x-2">
          {[0, 1].map((i) => (
            <div
              key={i}
              className={cn(
                "h-2 flex-1 rounded transition-colors",
                i <= currentStep ? "bg-brand-400" : "bg-brand-100",
              )}
            />
          ))}
        </div>

        <p className="mt-3 text-center text-sm text-gray-500">
          Step {currentStep + 1} of 2
        </p>

        <Outlet />
      </div>
    </QuickMatchContext.Provider>
  );
}
